import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type AdminService,
  type ServiceCategoryRecord,
} from "../lib/api.ts";
import { formatGbp } from "../lib/format.ts";
import {
  countWords,
  DESCRIPTION_MAX_CHARS,
  DESCRIPTION_MAX_WORDS,
} from "../lib/text.ts";
import { TreatmentImageField } from "./TreatmentImageField.tsx";
import { CategoriesEditor } from "./CategoriesEditor.tsx";

interface ServicesEditorProps {
  token: string;
  onUnauthorised: () => void;
}

interface FormState {
  categoryId: number;
  name: string;
  description: string;
  durationMinutes: string;
  pricePounds: string;
  isActive: boolean;
  isSignature: boolean;
}

function toForm(service: AdminService): FormState {
  return {
    categoryId: service.categoryId,
    name: service.name,
    description: service.description ?? "",
    durationMinutes: String(service.durationMinutes),
    pricePounds: (service.pricePence / 100).toFixed(2),
    isActive: service.isActive,
    isSignature: service.isSignature,
  };
}

function blankForm(categoryId: number): FormState {
  return {
    categoryId,
    name: "",
    description: "",
    durationMinutes: "60",
    pricePounds: "",
    isActive: true,
    isSignature: false,
  };
}

function problems(form: FormState): string[] {
  const found: string[] = [];
  if (form.name.trim().length < 2) found.push("Give the treatment a name.");
  const minutes = Number(form.durationMinutes);
  if (!Number.isInteger(minutes) || minutes < 5 || minutes > 480) {
    found.push("Duration must be between 5 and 480 minutes.");
  }
  const pounds = Number(form.pricePounds);
  if (!Number.isFinite(pounds) || pounds < 0 || pounds > 10_000) {
    found.push("Enter a price in pounds.");
  }
  // Counted the same way the API counts it, so the editor and the save agree.
  const words = countWords(form.description);
  if (words > DESCRIPTION_MAX_WORDS) {
    found.push(
      `The description is ${words.toLocaleString("en-GB")} words. ` +
        `The limit is ${DESCRIPTION_MAX_WORDS.toLocaleString("en-GB")}.`,
    );
  }
  return found;
}

export function ServicesEditor({ token, onUnauthorised }: ServicesEditorProps) {
  const [services, setServices] = useState<AdminService[] | null>(null);
  const [categories, setCategories] = useState<ServiceCategoryRecord[]>([]);
  const [depositPercent, setDepositPercent] = useState(50);
  const [imageStoreConfigured, setImageStoreConfigured] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [busyId, setBusyId] = useState<number | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.admin.getServices(token);
      setServices(result.services);
      setCategories(result.categories);
      setDepositPercent(result.depositPercent);
      setImageStoreConfigured(result.imageStoreConfigured);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError("Treatments could not be loaded.");
    }
  }, [token, onUnauthorised]);

  useEffect(() => {
    void load();
  }, [load]);

  function startEdit(service: AdminService) {
    setAdding(false);
    setEditingId(service.id);
    setForm(toForm(service));
    setError(null);
    setMessage(null);
  }

  function startAdd() {
    setEditingId(null);
    setAdding(true);
    setForm(blankForm(categories[0]?.id ?? 0));
    setError(null);
    setMessage(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setAdding(false);
    setForm(null);
  }

  async function submit() {
    if (!form) return;
    const pricePence = Math.round(Number(form.pricePounds) * 100);
    const payload = {
      categoryId: form.categoryId,
      name: form.name.trim(),
      description: form.description.trim() || null,
      durationMinutes: Number(form.durationMinutes),
      pricePence,
      isActive: form.isActive,
    };

    setBusyId(adding ? "new" : editingId);
    setError(null);
    try {
      if (adding) {
        const created = await api.admin.createService(token, {
          ...payload,
          description: payload.description ?? "",
        });
        setMessage(`${created.name} added. It is live on the website now.`);
      } else if (editingId !== null) {
        const updated = await api.admin.updateService(token, editingId, {
          ...payload,
          isSignature: form.isSignature,
        });
        setMessage(`${updated.name} updated.`);
      }
      cancelEdit();
      // A reload rather than patching the one row: ticking the signature here
      // unticks it on another treatment, which this response knows nothing about.
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "That could not be saved.",
      );
    } finally {
      setBusyId(null);
    }
  }

  async function toggleActive(service: AdminService) {
    const turningOff = service.isActive;
    if (
      turningOff &&
      !window.confirm(
        `Hide ${service.name} from the website and stop it being booked?`,
      )
    ) {
      return;
    }
    setBusyId(service.id);
    setError(null);
    try {
      await api.admin.updateService(token, service.id, {
        isActive: !service.isActive,
      });
      setMessage(`${service.name} is now ${turningOff ? "hidden" : "live"}.`);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError("That change could not be saved.");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(service: AdminService) {
    if (
      !window.confirm(
        `Delete ${service.name} permanently? This cannot be undone.`,
      )
    )
      return;

    setBusyId(service.id);
    setError(null);
    try {
      await api.admin.deleteService(token, service.id);
      setMessage(`${service.name} deleted.`);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      // The API refuses when bookings reference it and explains why, so the
      // message is worth showing verbatim rather than replacing.
      setError(
        caught instanceof ApiError
          ? caught.message
          : "That treatment could not be deleted.",
      );
    } finally {
      setBusyId(null);
    }
  }

  const formErrors = form ? problems(form) : [];
  const grouped = categories.map((category) => ({
    category,
    items: (services ?? []).filter((s) => s.categoryId === category.id),
  }));

  return (
    <section className="page stack-lg">
      <div className="treatments__head">
        <div>
          <h2 className="admin__section-title">Treatments</h2>
          <p className="field-hint">
            Add, edit and remove treatments. The deposit is {depositPercent}% of
            the price, so changing a price changes the deposit with it.
            Everything here is live on the website immediately - no redeploy.
          </p>
        </div>
        <button
          type="button"
          className="btn"
          onClick={startAdd}
          disabled={adding || categories.length === 0}
        >
          Add treatment
        </button>
      </div>

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      {/*
        Categories first: a treatment has to go in one, so it reads better to
        manage the groups before the things inside them. Editing a category
        reloads the treatment list, since the grouping and headings come from it.
      */}
      <details className="admin-disclosure">
        <summary>Categories and their descriptions</summary>
        <div className="admin-disclosure__body">
          <CategoriesEditor
            token={token}
            onUnauthorised={onUnauthorised}
            onChanged={load}
          />
        </div>
      </details>

      {adding && form && (
        <TreatmentForm
          form={form}
          categories={categories}
          depositPercent={depositPercent}
          errors={formErrors}
          busy={busyId === "new"}
          title="New treatment"
          submitLabel="Add treatment"
          onChange={setForm}
          onSubmit={submit}
          onCancel={cancelEdit}
        >
          <p className="field-hint">
            Add the treatment first, then edit it to upload a photograph.
          </p>
        </TreatmentForm>
      )}

      {!services && (
        <p className="booking__loading">
          <span className="spinner" /> Loading treatments…
        </p>
      )}

      {services &&
        grouped.map(({ category, items }) => (
          <section key={category.id}>
            <h3 className="booking__group-title">
              {category.name} · {items.length} treatment
              {items.length === 1 ? "" : "s"}
            </h3>

            {items.length === 0 && (
              <p className="field-hint">Nothing in this category yet.</p>
            )}

            <ul className="treatment-admin-list">
              {items.map((service) =>
                editingId === service.id && form ? (
                  <li key={service.id}>
                    <TreatmentForm
                      form={form}
                      categories={categories}
                      depositPercent={depositPercent}
                      errors={formErrors}
                      busy={busyId === service.id}
                      title={`Editing ${service.name}`}
                      submitLabel="Save changes"
                      signatureOption
                      onChange={setForm}
                      onSubmit={submit}
                      onCancel={cancelEdit}
                    >
                      <TreatmentImageField
                        token={token}
                        service={service}
                        storeConfigured={imageStoreConfigured}
                        onChanged={load}
                        onUnauthorised={onUnauthorised}
                      />
                    </TreatmentForm>
                  </li>
                ) : (
                  <li
                    key={service.id}
                    className={`treatment-admin ${service.isActive ? "" : "treatment-admin--hidden"}`}
                  >
                    <div className="treatment-admin__main">
                      {service.imageUrl && (
                        <span className="treatment-admin__thumb">
                          <img
                            src={service.imageUrl}
                            alt={service.imageAltText ?? service.name}
                            loading="lazy"
                          />
                        </span>
                      )}
                      <div>
                        <h4 className="treatment-admin__name">
                          {service.name}
                          {service.isSignature && (
                            <span className="pill">Signature</span>
                          )}
                          {!service.isActive && (
                            <span className="pill pill--dead">Hidden</span>
                          )}
                        </h4>
                        <p className="treatment-admin__meta">
                          {service.durationMinutes} min ·{" "}
                          {service.offerPricePence === null ? (
                            formatGbp(service.pricePence)
                          ) : (
                            <>
                              <s>{formatGbp(service.pricePence)}</s>{" "}
                              <strong>
                                {formatGbp(service.offerPricePence)}
                              </strong>
                            </>
                          )}{" "}
                          · {formatGbp(service.depositPence)} deposit
                          {service.offerLabel && (
                            <span className="treatment-admin__offer">
                              {service.offerLabel}
                            </span>
                          )}
                          {service.bookingCount > 0 && (
                            <>
                              {" "}
                              · {service.bookingCount} booking
                              {service.bookingCount === 1 ? "" : "s"}
                            </>
                          )}
                        </p>
                        {service.description ? (
                          <p className="treatment-admin__description">
                            {service.description}
                          </p>
                        ) : (
                          <p className="treatment-admin__description treatment-admin__description--empty">
                            No description - the website will show just the
                            name.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="treatment-admin__actions">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        disabled={busyId === service.id}
                        onClick={() => startEdit(service)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn--quiet"
                        disabled={busyId === service.id}
                        onClick={() => toggleActive(service)}
                      >
                        {service.isActive ? "Hide" : "Show"}
                      </button>
                      <button
                        type="button"
                        className="btn btn--danger"
                        disabled={busyId === service.id || !service.deletable}
                        title={
                          service.deletable
                            ? "Delete permanently"
                            : "Has bookings against it - hide it instead"
                        }
                        onClick={() => remove(service)}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ),
              )}
            </ul>
          </section>
        ))}
    </section>
  );
}

interface TreatmentFormProps {
  /** Slot for the image field, which needs a saved treatment to attach to. */
  children?: React.ReactNode;
  form: FormState;
  categories: ServiceCategoryRecord[];
  depositPercent: number;
  errors: string[];
  busy: boolean;
  title: string;
  submitLabel: string;
  /**
   * Whether to offer the signature tick. Not while adding: the signature is
   * featured with its photograph, and a photograph can only be added once the
   * treatment has been saved.
   */
  signatureOption?: boolean;
  onChange: (form: FormState) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

function TreatmentForm({
  children,
  form,
  categories,
  depositPercent,
  errors,
  busy,
  title,
  submitLabel,
  signatureOption = false,
  onChange,
  onSubmit,
  onCancel,
}: TreatmentFormProps) {
  const [touched, setTouched] = useState(false);
  const descriptionWords = countWords(form.description);
  const pence = Math.round(Number(form.pricePounds) * 100);
  const deposit = Number.isFinite(pence)
    ? Math.max(0, Math.min(pence, Math.round((pence * depositPercent) / 100)))
    : 0;

  return (
    <form
      className="panel admin-form"
      onSubmit={(event) => {
        event.preventDefault();
        setTouched(true);
        if (errors.length === 0) onSubmit();
      }}
    >
      <h3 className="admin-form__title">{title}</h3>

      <div className="admin-form__grid">
        <div className="field">
          <label htmlFor="tf-name">Name</label>
          <input
            id="tf-name"
            className="input"
            value={form.name}
            maxLength={120}
            onChange={(event) =>
              onChange({ ...form, name: event.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="tf-category">Category</label>
          <select
            id="tf-category"
            className="select"
            value={form.categoryId}
            onChange={(event) =>
              onChange({ ...form, categoryId: Number(event.target.value) })
            }
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="tf-duration">Duration (minutes)</label>
          <input
            id="tf-duration"
            className="input"
            type="number"
            min={5}
            max={480}
            step={5}
            value={form.durationMinutes}
            onChange={(event) =>
              onChange({ ...form, durationMinutes: event.target.value })
            }
          />
          <p className="field-hint">
            Booking slots are offered around this length.
          </p>
        </div>

        <div className="field">
          <label htmlFor="tf-price">Price (£)</label>
          <input
            id="tf-price"
            className="input"
            type="number"
            min={0}
            step="0.01"
            value={form.pricePounds}
            onChange={(event) =>
              onChange({ ...form, pricePounds: event.target.value })
            }
          />
          <p className="field-hint">Deposit will be {formatGbp(deposit)}.</p>
        </div>
      </div>

      <div className="field">
        <label htmlFor="tf-description">Description</label>
        <textarea
          id="tf-description"
          className="textarea textarea--tall"
          rows={12}
          /*
            The real limit is the word count below. This character cap is only a
            backstop against pasting something absurd, and sits well clear of
            2000 words of normal prose.
          */
          maxLength={DESCRIPTION_MAX_CHARS}
          value={form.description}
          aria-invalid={descriptionWords > DESCRIPTION_MAX_WORDS}
          aria-describedby="tf-description-count"
          placeholder="What the treatment involves, who it suits, what to expect."
          onChange={(event) =>
            onChange({ ...form, description: event.target.value })
          }
        />
        <p
          id="tf-description-count"
          className={
            descriptionWords > DESCRIPTION_MAX_WORDS
              ? "field-error"
              : "field-hint"
          }
        >
          {descriptionWords.toLocaleString("en-GB")} of{" "}
          {DESCRIPTION_MAX_WORDS.toLocaleString("en-GB")} words
          {descriptionWords > DESCRIPTION_MAX_WORDS
            ? ` - ${(descriptionWords - DESCRIPTION_MAX_WORDS).toLocaleString("en-GB")} over the limit.`
            : " - shown under the treatment name on the website."}
        </p>
      </div>

      {children}

      <label className="hours-editor__toggle">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(event) =>
            onChange({ ...form, isActive: event.target.checked })
          }
        />
        <span>Show on the website and allow bookings</span>
      </label>

      {signatureOption && (
        <div>
          <label className="hours-editor__toggle">
            <input
              type="checkbox"
              checked={form.isSignature}
              aria-describedby="tf-signature-hint"
              onChange={(event) =>
                onChange({ ...form, isSignature: event.target.checked })
              }
            />
            <span>Signature treatment</span>
          </label>
          <p id="tf-signature-hint" className="field-hint">
            Featured large at the top of the treatments on the homepage. Only
            one treatment can be the signature, so ticking this one unticks the
            current one.
          </p>
        </div>
      )}

      {touched && errors.length > 0 && (
        <ul className="admin-form__errors">
          {errors.map((problem) => (
            <li key={problem} className="field-error">
              {problem}
            </li>
          ))}
        </ul>
      )}

      <div className="row">
        <button type="submit" className="btn" disabled={busy}>
          {busy ? <span className="spinner" /> : submitLabel}
        </button>
        <button
          type="button"
          className="btn btn--quiet"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
